import {
  buildTaskColumnApiField,
  parseTaskColumnPath,
  type ColumnConfigEntry,
  type SystemEntity,
} from '@erp/shared-types';
import type { TaskAnchorFieldCatalogEntry } from '@erp/rpc';

/**
 * Anchor columns that hold another record's id (an AP invoice's `supplierId`).
 * The cell shows that record by name and the toolbar offers a picker that
 * filters the queue on it. Pure helpers, kept free of React imports.
 */
export type AnchorReferenceColumn = {
  columnId: string;
  header: string;
  /** Catalog path under `anchor.` (e.g. `supplierId`). */
  path: string;
  entityType: SystemEntity;
};

/** Catalog path → the entity type its value references. */
export function anchorReferencesByPath(
  catalog: readonly TaskAnchorFieldCatalogEntry[] | undefined,
): Map<string, SystemEntity> {
  const references = new Map<string, SystemEntity>();
  for (const entry of catalog ?? []) {
    if (entry.references) references.set(entry.path, entry.references);
  }
  return references;
}

/** Visible anchor columns whose path is a reference field, in column order. */
export function anchorReferenceColumns(
  columns: readonly ColumnConfigEntry[],
  referencesByPath: ReadonlyMap<string, SystemEntity>,
): AnchorReferenceColumn[] {
  return columns.flatMap((column) => {
    if (column.hidden) return [];
    const parsed = parseTaskColumnPath(column.apiField);
    if (parsed?.source !== 'anchor') return [];
    const path = parsed.segments.join('.');
    const entityType = referencesByPath.get(path);
    if (!entityType) return [];
    return [{ columnId: column.id, header: column.header, path, entityType }];
  });
}

/** apiField → referenced entity type, for rendering the cells. */
export function referenceEntityByApiField(
  columns: readonly AnchorReferenceColumn[],
): Map<string, SystemEntity> {
  return new Map(
    columns.map((column) => [
      buildTaskColumnApiField('anchor', column.path),
      column.entityType,
    ]),
  );
}

/**
 * The URL's `ref` picks whose column is shown, so the queue never filters on
 * something the toolbar no longer offers. Undefined when none apply.
 */
export function activeReferenceSelections(
  ref: Readonly<Record<string, string>> | undefined,
  columns: readonly AnchorReferenceColumn[],
): Record<string, string> | undefined {
  if (!ref) return undefined;
  const paths = new Set(columns.map((column) => column.path));
  const active: Record<string, string> = {};
  for (const [path, id] of Object.entries(ref)) {
    if (id && paths.has(path)) active[path] = id;
  }
  return Object.keys(active).length > 0 ? active : undefined;
}
